import React, { useState, useEffect } from "react";
import { Divider, ClickAwayListener } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import colors from "assets/theme/base/colors";
import MDBox from "components/atoms/MDBox/MDBox";
import MDTypography from "components/atoms/MDTypography/MDTypography";
import {
    DateRangePickerContainer,
    TriggerButton,
    DropdownContainer,
    LayoutContainer,
    LeftColumn,
    RightColumn,
    PresetButton,
    ActionButton,
    CalendarContainer,
    CalendarGrid,
    CalendarHeader,
    NavigationButton,
    DayHeader,
    DayButton,
    CustomRangeInputs,
    DateInputRow,
    DateInput
} from "./DateRangePicker.styles";


export interface DateRange {
    startDate: Date | null;
    endDate: Date | null;
}

interface DateRangePickerProps {
    value?: DateRange;
    onChange: (dateRange: DateRange) => void;
    placeholder?: string;
}

const presetOptions = [
    { label: "Single Day", key: "single" },
    { label: "Last 3 days", key: "last3" },
    { label: "Last 7 days", key: "last7" },
    { label: "Next 3 days", key: "next3" },
    { label: "Next 7 days", key: "next7" },
    { label: "Custom Range", key: "custom" },
];

const DateRangePicker: React.FC<DateRangePickerProps> = ({
    value,
    onChange,
    placeholder = "Date",
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState<string>("custom");
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [rangeStart, setRangeStart] = useState<Date | null>(value?.startDate || null);
    const [rangeEnd, setRangeEnd] = useState<Date | null>(value?.endDate || null);

    // Sync internal state when the controlled value prop changes (e.g., clearing filters)
    useEffect(() => {
        const nextStart = value?.startDate || null;
        const nextEnd = value?.endDate || null;
        setRangeStart(nextStart);
        setRangeEnd(nextEnd);
        if (!nextStart && !nextEnd) {
            setSelectedPreset("custom");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value?.startDate, value?.endDate]);

    const getPresetDateRange = (preset: string): DateRange => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (preset) {
            case "single":
                return { startDate: today, endDate: today };
            case "last3": {
                const last3 = new Date(today);
                last3.setDate(today.getDate() - 2);
                return { startDate: last3, endDate: today };
            }
            case "last7": {
                const last7 = new Date(today);
                last7.setDate(today.getDate() - 6);
                return { startDate: last7, endDate: today };
            }
            case "next3": {
                const next3 = new Date(today);
                next3.setDate(today.getDate() + 2);
                return { startDate: today, endDate: next3 };
            }
            case "next7": {
                const next7 = new Date(today);
                next7.setDate(today.getDate() + 6);
                return { startDate: today, endDate: next7 };
            }
            default:
                return { startDate: null, endDate: null };
        }
    };

    const handlePresetSelect = (preset: string) => {
        setSelectedPreset(preset);
        if (preset !== "custom") {
            const dateRange = getPresetDateRange(preset);
            setRangeStart(dateRange.startDate);
            setRangeEnd(dateRange.endDate);
            onChange(dateRange);
            setIsOpen(false);
        }
    };

    const handleDateClick = (date: Date) => {
        if (selectedPreset !== "custom") {
            setSelectedPreset("custom");
        }

        if (!rangeStart || (rangeStart && rangeEnd)) {
            // Start new range
            setRangeStart(date);
            setRangeEnd(null);
        } else if (rangeStart && !rangeEnd) {
            // Complete range
            if (date < rangeStart) {
                setRangeStart(date);
                setRangeEnd(rangeStart);
            } else {
                setRangeEnd(date);
            }
        }
    };

    const handleSubmit = () => {
        onChange({ startDate: rangeStart, endDate: rangeEnd });
        setIsOpen(false);
    };

    const handleCancel = () => {
        // Reset internal state to match the committed value
        setRangeStart(value?.startDate || null);
        setRangeEnd(value?.endDate || null);
        setSelectedPreset("custom");
        setIsOpen(false);
    };

    const formatDateRange = () => {
        // Use the committed value (props) for display, not internal state
        const committedStart = value?.startDate;
        const committedEnd = value?.endDate;

        if (!committedStart && !committedEnd) return placeholder;

        const formatDate = (date: Date) => {
            return date.toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric"
            });
        };

        if (committedStart && committedEnd) {
            if (committedStart.getTime() === committedEnd.getTime()) {
                return formatDate(committedStart);
            }
            return `${formatDate(committedStart)} - ${formatDate(committedEnd)}`;
        } else if (committedStart) {
            return formatDate(committedStart);
        }

        return placeholder;
    };

    const generateCalendarDays = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        // const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < 42; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);

            const isCurrentMonth = date.getMonth() === month;
            const isToday = date.getTime() === today.getTime();
            const isSelected = Boolean((rangeStart && date.getTime() === rangeStart.getTime()) ||
                (rangeEnd && date.getTime() === rangeEnd.getTime()));
            const isInRange = Boolean(rangeStart && rangeEnd &&
                date >= rangeStart && date <= rangeEnd && !isSelected);

            days.push({
                date,
                isCurrentMonth,
                isToday,
                isSelected,
                isInRange,
            });
        }

        return days;
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(currentMonth.getMonth() + (direction === 'next' ? 1 : -1));
        setCurrentMonth(newMonth);
    };

    return (
        <DateRangePickerContainer>
            <TriggerButton onClick={() => setIsOpen(!isOpen)}>
                <FilterListIcon fontSize="small" sx={{ mr: 1 }} />
                <span>{formatDateRange()}</span>
            </TriggerButton>

            {isOpen && (
                <ClickAwayListener onClickAway={() => setIsOpen(false)}>
                    <DropdownContainer>
                        <LayoutContainer>
                            {/* Left Column: Preset Options and Custom Inputs */}
                            <LeftColumn>
                                {/* Preset Options */}
                                <MDBox mb={2}>
                                    {presetOptions.map((option) => (
                                        <PresetButton
                                            key={option.key}
                                            selected={selectedPreset === option.key}
                                            onClick={() => handlePresetSelect(option.key)}
                                        >
                                            {option.label}
                                        </PresetButton>
                                    ))}
                                </MDBox>

                                <Divider sx={{ mb: 2 }} />

                                {/* Custom Range Inputs */}
                                <CustomRangeInputs>
                                    <DateInputRow>
                                        <MDTypography variant="caption" fontWeight="medium">From:</MDTypography>
                                        <DateInput
                                            type="date"
                                            value={rangeStart ? rangeStart.toISOString().split('T')[0] : ''}
                                            disabled={selectedPreset !== "custom"}
                                            onChange={(e) => {
                                                const date = e.target.value ? new Date(e.target.value) : null;
                                                setRangeStart(date);
                                            }}
                                        />
                                    </DateInputRow>
                                    <DateInputRow>
                                        <MDTypography variant="caption" fontWeight="medium">To:</MDTypography>
                                        <DateInput
                                            type="date"
                                            value={rangeEnd ? rangeEnd.toISOString().split('T')[0] : ''}
                                            disabled={selectedPreset !== "custom"}
                                            onChange={(e) => {
                                                const date = e.target.value ? new Date(e.target.value) : null;
                                                setRangeEnd(date);
                                            }}
                                        />
                                    </DateInputRow>
                                </CustomRangeInputs>
                            </LeftColumn>

                            {/* Right Column: Calendar */}
                            <RightColumn>
                                <CalendarContainer>
                                    <CalendarHeader>
                                        <NavigationButton onClick={() => navigateMonth('prev')}>
                                            <ChevronLeftIcon fontSize="medium" sx={{ color: colors.secondary.main }} />
                                        </NavigationButton>
                                        <MDTypography variant="subtitle2" fontWeight="medium">
                                            {currentMonth.toLocaleDateString("en-US", {
                                                month: "long",
                                                year: "numeric"
                                            })}
                                        </MDTypography>
                                        <NavigationButton onClick={() => navigateMonth('next')}>
                                            <ChevronRightIcon fontSize="medium" sx={{ color: colors.secondary.main }} />
                                        </NavigationButton>
                                    </CalendarHeader>

                                    <CalendarGrid>
                                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                                            <DayHeader key={day}>{day}</DayHeader>
                                        ))}

                                        {generateCalendarDays().map((day, index) => (
                                            <DayButton
                                                key={index}
                                                isSelected={day.isSelected}
                                                isInRange={day.isInRange}
                                                isToday={day.isToday}
                                                isDisabled={!day.isCurrentMonth}
                                                onClick={() => day.isCurrentMonth && handleDateClick(day.date)}
                                                disabled={!day.isCurrentMonth}
                                            >
                                                {day.date.getDate()}
                                            </DayButton>
                                        ))}
                                    </CalendarGrid>

                                </CalendarContainer>
                            </RightColumn>
                        </LayoutContainer>
                        {selectedPreset === "custom" && (
                            <MDBox mt={2} display="flex" gap={1} justifyContent="flex-end">
                                <ActionButton
                                    size="small"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </ActionButton>
                                <ActionButton
                                    size="small"
                                    onClick={handleSubmit}
                                    disabled={!rangeStart || !rangeEnd}
                                >
                                    Submit
                                </ActionButton>
                            </MDBox>
                        )}
                    </DropdownContainer>
                </ClickAwayListener>
            )}
        </DateRangePickerContainer>
    );
};

export default DateRangePicker;
